import { LoadingState } from '@lcs/shared/api';
import { EntityState } from '@ngrx/entity';
import { createFeatureSelector, createSelector, on } from '@ngrx/store';
import { OrderingDirections, OrderingOption } from 'generated/graphql-types';
import { RegisterState } from 'src/app/common-components/register/models/register-state';
import {
   RegisterAdapter,
   createRegisterAdapter,
} from 'src/app/common-components/register/models/register-state.adapter';
import { confirmDeviceChange } from 'src/app/pages/device-details/state/device-detail.actions';
import { extensionDetailLoad } from 'src/app/pages/extension-details/state/extension-detail.actions';
import * as AddPhoneActions from '../../../add-phone/state/add-phone.actions';
import { PhoneRegisterOptions } from '../../models/phone-register-options';
import { PhoneRegisterListItem } from '../../models/phone-register-results.model';
import * as extensionPhoneRegisterActions from './extension-phone-register.actions';

export const extensionPhoneRegisterFeatureKey = 'extensionPhoneRegister';
interface PhoneRegisterState
   extends EntityState<PhoneRegisterListItem>,
      RegisterState<PhoneRegisterListItem, PhoneRegisterOptions, OrderingOption> {
   phones: PhoneRegisterListItem[];
   invalidPhoneIds?: number[];
}

const adapter: RegisterAdapter<PhoneRegisterListItem, PhoneRegisterOptions, OrderingOption> = createRegisterAdapter<
   PhoneRegisterListItem,
   PhoneRegisterOptions,
   OrderingOption
>({
   selectId: (phoneRegisterItem) => {
      return phoneRegisterItem.phoneId ?? 0;
   },
});

const initialState: PhoneRegisterState = {
   ...adapter.getInitialState(),
   phones: [],
   invalidPhoneIds: [],
   orderingOptions: [{ direction: OrderingDirections.Asc, field: 'phoneTypeName' }],
};

export const extensionPhoneRegisterReducer = adapter.createReducer(
   extensionPhoneRegisterActions.extensionPhoneRegisterAdapterActions,
   initialState,
   on(extensionPhoneRegisterActions.extensionPhoneRegisterAdapterActions.pageEnter, extensionDetailLoad, (state) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         orderingOptions: [{ direction: OrderingDirections.Asc, field: 'phoneTypeName' }],
      };
   }),
   on(
      AddPhoneActions.savePhoneSuccess,
      extensionPhoneRegisterActions.deleteExtensionPhoneConfirm,
      confirmDeviceChange,
      (state) => {
         return {
            ...state,
            callState: LoadingState.Loading,
         };
      }
   ),
   on(extensionPhoneRegisterActions.extensionPhoneRegisterAdapterActions.loadSuccess, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loaded,
         phones: action.items,
         endCursor: action.endCursor,
         hasNextPage: action.hasNextPage,
         totalCount: action.totalCount,
      };
   }),
   on(AddPhoneActions.addPhoneToRegister, (state, action) => {
      let phone = action.phone;
      let phoneId = phone.phoneId ?? 0;
      let existingPhone = state.entities[phoneId];
      if (existingPhone) {
         return {
            ...state,
            callState: LoadingState.Loaded,
            phones: [...state.phones.filter((p) => p.phoneId !== phoneId), phone],
         };
      } else {
         return {
            ...state,
            callState: LoadingState.Loaded,
            phones: [...state.phones, action.phone],
         };
      }
   }),
   on(extensionPhoneRegisterActions.deleteExtensionPhone, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loaded,
         phones: state.phones.filter((p) => p.phoneId !== action.phoneId),
      };
   }),
   on(extensionPhoneRegisterActions.updateInvalidPhones, (state, action) => {
      return {
         ...state,
         invalidPhoneIds: action.invalidPhoneIds,
      };
   }),
   on(extensionPhoneRegisterActions.extensionPhoneRegisterAdapterActions.orderingOptionsChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         orderingOptions: state.orderingOptions
            ? [
                 ...state.orderingOptions
                    .filter((option) => !action.orderingOptions.some((newOption) => newOption.field === option.field))
                    .filter((option) => option.direction !== OrderingDirections.NotSet),
                 ...action.orderingOptions,
              ]
            : action.orderingOptions,
      };
   })
);
export const extensionPhoneRegisterSelector = createFeatureSelector<PhoneRegisterState>(
   extensionPhoneRegisterFeatureKey
);
export const selectors = adapter.getSelectors(extensionPhoneRegisterSelector);
export const { selectAll, selectCallState, selectTotalCount, selectEndCursor, selectOrderingOptions } =
   adapter.getSelectors(extensionPhoneRegisterSelector);

export const selectPhones = createSelector(extensionPhoneRegisterSelector, (state) => state.phones);

export const selectInvalidPhoneIds = createSelector(extensionPhoneRegisterSelector, (state) => state.invalidPhoneIds);
